import { Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TimeSelect } from "@/components/TimeSelect";
import type { DaySchedule } from "@/lib/api/types";
import { DAYS } from "@/lib/utils/schedule";
import { cn } from "@/lib/utils";

// Compact deal schedule picker — a single shared time window applied to every
// active day (all-day during business hours, or one restricted window). Mirrors
// disca-merchant-react's DealScheduleInput exactly so the deal forms share one
// schedule UX across the platform.

const ALL_DAY: DaySchedule = { open: "00:00", close: "23:59" };
const DEFAULT_RESTRICTED: DaySchedule = { open: "09:00", close: "22:00" };

const DAY_LABELS: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"] as const;
const WEEKEND = ["saturday", "sunday"] as const;

type Preset = "everyday" | "weekdays" | "weekends";

type DealScheduleInputProps = {
  schedule: Record<string, DaySchedule[]>;
  onChange: (schedule: Record<string, DaySchedule[]>) => void;
  error?: string;
  disabled?: boolean;
};

function isAllDaySlot(slots: DaySchedule[]): boolean {
  return slots.length === 1 && slots[0].open === ALL_DAY.open && slots[0].close === ALL_DAY.close;
}

export function DealScheduleInput({
  schedule,
  onChange,
  error,
  disabled = false,
}: DealScheduleInputProps) {
  const activeDays = new Set(
    Object.entries(schedule)
      .filter(([, slots]) => slots.length > 0)
      .map(([d]) => d)
  );

  const isAllDay =
    activeDays.size === 0 || [...activeDays].every((day) => isAllDaySlot(schedule[day] ?? []));

  // Current restricted time slot (from first non-all-day active day, or default).
  const firstRestrictedDay = DAYS.find(
    (d) => activeDays.has(d) && !isAllDaySlot(schedule[d] ?? [])
  );
  const currentSlot = firstRestrictedDay ? schedule[firstRestrictedDay][0] : DEFAULT_RESTRICTED;

  const currentPreset: Preset | null = (() => {
    if (DAYS.every((d) => activeDays.has(d))) return "everyday";
    if (WEEKDAYS.every((d) => activeDays.has(d)) && WEEKEND.every((d) => !activeDays.has(d)))
      return "weekdays";
    if (WEEKEND.every((d) => activeDays.has(d)) && WEEKDAYS.every((d) => !activeDays.has(d)))
      return "weekends";
    return null;
  })();

  function makeSlot(): DaySchedule[] {
    return isAllDay ? [{ ...ALL_DAY }] : [{ open: currentSlot.open, close: currentSlot.close }];
  }

  function toggleDay(day: string) {
    const next = { ...schedule };
    if (activeDays.has(day)) {
      delete next[day];
    } else {
      next[day] = makeSlot();
    }
    onChange(next);
  }

  function applyPreset(preset: Preset) {
    const days: readonly string[] =
      preset === "everyday" ? DAYS : preset === "weekdays" ? WEEKDAYS : WEEKEND;
    const slot = makeSlot();
    const next: Record<string, DaySchedule[]> = {};
    for (const day of days) next[day] = slot;
    onChange(next);
  }

  function toggleHours() {
    if (activeDays.size === 0) return;
    const next: Record<string, DaySchedule[]> = {};
    for (const day of activeDays) {
      next[day] = isAllDay ? [{ ...DEFAULT_RESTRICTED }] : [{ ...ALL_DAY }];
    }
    onChange(next);
  }

  function handleTimeChange(open: string, close: string) {
    const next: Record<string, DaySchedule[]> = {};
    for (const day of activeDays) next[day] = [{ open, close }];
    onChange(next);
  }

  return (
    <fieldset disabled={disabled} className="space-y-3 disabled:opacity-50">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Active Schedule</p>
          <p className="text-xs text-muted-foreground">
            Select days when this deal is automatically active
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 pt-0.5">
          {(["everyday", "weekdays", "weekends"] as Preset[]).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => applyPreset(preset)}
              className={cn(
                "flex items-center gap-1.5 text-xs transition-colors whitespace-nowrap",
                currentPreset === preset
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span
                className={cn(
                  "size-2 rounded-full shrink-0",
                  currentPreset === preset ? "bg-primary" : "bg-muted-foreground/40"
                )}
              />
              {preset === "everyday"
                ? "Every day"
                : preset === "weekdays"
                  ? "Weekdays"
                  : "Weekends"}
            </button>
          ))}
        </div>
      </div>

      {/* Day pills */}
      <div className="flex gap-1.5 w-full">
        {DAYS.map((day) => {
          const isActive = activeDays.has(day);
          return (
            <button
              key={day}
              type="button"
              onClick={() => toggleDay(day)}
              aria-label={`Toggle ${day}`}
              aria-pressed={isActive}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-11 rounded-lg text-xs font-semibold transition-colors gap-0.5 min-w-0",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "border border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
              )}
            >
              {DAY_LABELS[day]}
              <span
                className={cn(
                  "size-1 rounded-full",
                  isActive ? "bg-primary-foreground/60" : "bg-transparent"
                )}
              />
            </button>
          );
        })}
      </div>

      {/* Time window */}
      <div className="border rounded-lg px-3 py-1.5 flex items-center justify-between gap-3 bg-background">
        <div className="flex items-center gap-1.5 min-w-0">
          <Clock className="size-3.5 text-muted-foreground shrink-0" />
          {isAllDay ? (
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className="text-xs font-medium shrink-0">Time window</span>
              <span className="text-xs text-muted-foreground truncate">
                All day during business hours
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-medium shrink-0">Time window</span>
              <TimeSelect
                value={currentSlot.open}
                onChange={(v) => handleTimeChange(v, currentSlot.close)}
                disabled={disabled}
              />
              <span className="text-xs text-muted-foreground">–</span>
              <TimeSelect
                value={currentSlot.close}
                onChange={(v) => handleTimeChange(currentSlot.open, v)}
                disabled={disabled}
              />
            </div>
          )}
        </div>
        {isAllDay ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleHours}
            disabled={activeDays.size === 0}
            className="shrink-0 h-7 text-xs px-2.5 border-muted-foreground/40"
          >
            Restrict Hours
          </Button>
        ) : (
          <button
            type="button"
            onClick={toggleHours}
            className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Remove time restriction"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </fieldset>
  );
}
